import React, { useState, useEffect } from 'react';
import './CorteCaja.css';
import { useAuth } from '../../contexts/AuthContext';
import API from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import Header from '../common/Header';
import Footer from '../common/Footer';
import AutorizacionModal from '../Auth/AutorizacionModal';
import { FaFileInvoiceDollar, FaRegListAlt, FaSearchDollar, FaCalendarAlt, FaCashRegister, FaListUl, FaPrint, FaCalculator } from 'react-icons/fa';

const CorteCaja = () => {
  const navigate = useNavigate();

  // Estado para información del negocio
  const [infoNegocio, setInfoNegocio] = useState({
    nombre_negocio: 'KAPITAL SUSHI',
    nombre_sucursal: 'BONATERRA',
    direccion_linea1: 'AV. JOSE MARIA CHAVEZ #2303',
    direccion_linea2: 'COL. SAN FRANCISCO DEL ARENAL',
    razon_social: 'OPERADORA ASR S.A DE C.V.',
    rfc: 'OAC1404108V8'
  });

  // Estado para opciones de impresión
  const [opcionesImpresion, setOpcionesImpresion] = useState({
    imprimirProductosVendidos: false,
    imprimirProductosCancelados: true,
    imprimirProductosMalaOrden: true,
    imprimirProductosConGarantia: false,
    imprimirProductosAgrupados: false,
    imprimirGastos: true,
    imprimirCancelados: true
  });

  // Estado para arqueo de caja (denominaciones según tu tabla)
  const [arqueoCaja, setArqueoCaja] = useState({
    '1000.00': 0,
    '500.00': 0,
    '200.00': 0,
    '100.00': 0,
    '50.00': 0,
    '20.00': 0,
    '10.00': 0,
    '5.00': 0,
    '2.00': 0,
    '1.00': 0,
    '0.50': 0
  });

  // Estado para información del corte
  const [infoCorte, setInfoCorte] = useState({
    siguienteCorte: 0,
    ultimoCorte: {
      fecha: '',
      hora: ''
    },
    fechaActual: '',
    horaActual: ''
  });

  // Estado para consulta de cortes anteriores
  const [fechaConsulta, setFechaConsulta] = useState('');
  const [cortesAnteriores, setCortesAnteriores] = useState([]);
  const [totalArqueo, setTotalArqueo] = useState(0);

  // Estado para modal de autorización
  const [mostrarAutorizacion, setMostrarAutorizacion] = useState(false);

  // Estado para confirmar corte final (reemplaza window.confirm)
  const [mostrarConfirmCorteFinal, setMostrarConfirmCorteFinal] = useState(false);

  // Cargar información inicial
  useEffect(() => {
    cargarInformacionInicial();
  }, []);

  const cargarInformacionInicial = async () => {
    try {
      console.log("Cargando información de cortes...");

      // Obtener último corte, siguiente número e información del negocio
      const [ultimoCorteResponse, siguienteCorteResponse, infoNegocioResponse] = await Promise.all([
        API.cortesCaja.obtenerUltimoCorte(),
        API.cortesCaja.obtenerSiguienteCorte(),
        API.infoNegocio.obtenerInfo()
      ]);

      // Actualizar información del negocio si se obtuvo correctamente
      if (infoNegocioResponse.success && infoNegocioResponse.data) {
        setInfoNegocio(infoNegocioResponse.data);
        console.log("✅ Información del negocio cargada:", infoNegocioResponse.data);
      }

      // Actualizar fecha y hora actual primero
      const ahora = new Date();
      const fechaActualFormateada = ahora.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      const horaActualFormateada = ahora.toLocaleTimeString('es-ES');

      setInfoCorte(prev => ({
        ...prev,
        fechaActual: fechaActualFormateada,
        horaActual: horaActualFormateada
      }));

      // Establecer fecha de consulta a hoy en formato YYYY-MM-DD
      const fechaHoy = ahora.toISOString().split('T')[0];
      setFechaConsulta(fechaHoy);

      // Actualizar información del último corte
      if (ultimoCorteResponse.success && ultimoCorteResponse.data) {
        setInfoCorte(prev => ({
          ...prev,
          ultimoCorte: {
            fecha: ultimoCorteResponse.data.fecha || '',
            hora: ultimoCorteResponse.data.hora || ''
          }
        }));
      } else {
        console.log('No hay cortes anteriores o error al obtener último corte');
      }

      // Actualizar siguiente número de corte
      if (siguienteCorteResponse.success && siguienteCorteResponse.data) {
        setInfoCorte(prev => ({
          ...prev,
          siguienteCorte: siguienteCorteResponse.data.siguiente_corte || 1
        }));
      } else {
        console.log('Error al obtener siguiente número de corte, usando 1 por defecto');
        setInfoCorte(prev => ({
          ...prev,
          siguienteCorte: 1
        }));
      }

    } catch (error) {
      console.error("Error al cargar información inicial:", error);
    }
  };

  // Actualizar total de arqueo cuando cambian los valores
  useEffect(() => {
    let total = 0;
    Object.entries(arqueoCaja).forEach(([denominacion, cantidad]) => {
      total += parseFloat(denominacion) * cantidad;
    });
    setTotalArqueo(total);
  }, [arqueoCaja]);

  // Manejar cambios en las opciones de impresión
  const handleOpcionImpresionChange = (e) => {
    const { name, checked } = e.target;
    setOpcionesImpresion(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  // Manejar cambios en los campos de arqueo
  const handleArqueoChange = (e, denominacion) => {
    const valor = parseInt(e.target.value) || 0;
    setArqueoCaja(prev => ({
      ...prev,
      [denominacion]: valor
    }));
  };

  // Manejar foco en input - seleccionar todo el contenido
  const handleInputFocus = (e) => {
    e.target.select();
  };

  // Manejar tecla Enter para pasar al siguiente input
  const handleKeyDown = (e, currentDenominacion) => {
    if (e.key === 'Enter') {
      const denominaciones = Object.keys(arqueoCaja);
      const currentIndex = denominaciones.indexOf(currentDenominacion);
      const nextIndex = currentIndex + 1;

      if (nextIndex < denominaciones.length) {
        const nextDenominacion = denominaciones[nextIndex];
        const nextInput = document.querySelector(`input[data-denominacion="${nextDenominacion}"]`);
        if (nextInput) {
          nextInput.focus();
          nextInput.select();
        }
      }
    }
  };

  // Manejar el cambio en la fecha de consulta
  const handleFechaConsultaChange = (e) => {
    setFechaConsulta(e.target.value);
  };

  // Buscar cortes anteriores
  const buscarCortesAnteriores = async () => {
    try {
      console.log(`🔍 Buscando cortes para la fecha: ${fechaConsulta}`);

      if (!fechaConsulta) {
        mostrarAlertaPersonalizada("Por favor seleccione una fecha para consultar", 'warning');
        return;
      }

      // El input tipo date ya devuelve formato YYYY-MM-DD que necesita la BD
      const response = await API.cortesCaja.consultarCortesPorFecha(fechaConsulta);

      if (response.success) {
        setCortesAnteriores(response.data);
        console.log(`✅ Encontrados ${response.data.length} cortes para la fecha ${fechaConsulta}`);

        if (response.data.length === 0) {
          mostrarAlertaPersonalizada(`No se encontraron cortes para la fecha ${fechaConsulta}`, 'info');
        } else {
          mostrarAlertaPersonalizada(`Se encontraron ${response.data.length} corte(s) para la fecha ${fechaConsulta}`, 'success');
        }
      } else {
        console.error("Error al buscar cortes:", response.message);
        setCortesAnteriores([]);
        mostrarAlertaPersonalizada("Error al buscar cortes: " + response.message, 'error');
      }
    } catch (error) {
      console.error("Error al buscar cortes anteriores:", error);
      setCortesAnteriores([]);
      mostrarAlertaPersonalizada("Error de comunicación al buscar cortes", 'error');
    }
  };

  // Función para solicitar autorización para corte parcial
  const solicitarAutorizacionCorteParcial = () => {
    setMostrarAutorizacion(true);
  };

  // Manejar autorización exitosa para corte parcial
  const handleAutorizacionExitosa = (datosAutorizacion) => {
    setMostrarAutorizacion(false);
    // Proceder con el corte parcial
    realizarCorteParcial();
  };

  // Cerrar modal de autorización
  const cerrarAutorizacion = () => {
    setMostrarAutorizacion(false);
  };

  // Función para reimprimir un corte anterior
  const reimprimirCorte = async (corte) => {
    try {
      console.log("🖨️ Reimprimiendo corte:", corte);

      // Crear estructura de ticket básica para reimpresión
      const ticketReimpresion = {
        tipo: corte.tipo === 'final' ? 'corte_final' : 'corte_parcial',
        header: {
          titulo: 'KAPITAL SUSHI BONATERRA',
          subtitulo: corte.tipo === 'final' ? '** CORTE DE CAJA FINAL **' : '** CORTE DE CAJA PARCIAL **',
          numero_corte: corte.numero_corte,
          fecha: corte.fecha,
          hora: corte.hora,
          usuario: corte.usuario,
          fechaAnterior: corte.fechaAnterior || '00/00/0000',
          horaAnterior: corte.horaAnterior || '00:00:00'
        },
        totales: {
          total_efectivo: corte.total_efectivo,
          total_ventas: corte.total_ventas,
          fondo_caja: 0,
          gastos_efectivo: 0,
          diferencia_efectivo: 0
        },
        cuentas: {
          total_periodo: corte.total_mesas,
          pendientes_cobro: 0,
          por_tipo: {}
        },
        formas_pago: {},
        estadisticas: {
          ticket_promedio: corte.ticket_promedio || 0,
          ticket_promedio_mesa: 0,
          ticket_promedio_persona: 0,
          personas_atendidas: 0
        },
        ventas_por_usuario: {},
        gastos: [],
        platillos_cancelados: [],
        total_cancelados: 0,
        numeros_delivery: {},
        arqueo: []
      };

      const impresionExitosa = await imprimirTicketCorte(ticketReimpresion);

      if (impresionExitosa) {
        mostrarAlertaPersonalizada(`Corte #${corte.numero_corte} reimpreso exitosamente`, 'success');
      } else {
        mostrarAlertaPersonalizada(`Error al reimprimir corte #${corte.numero_corte}`, 'error');
      }
    } catch (error) {
      console.error("Error al reimprimir corte:", error);
      mostrarAlertaPersonalizada(`Error al reimprimir corte #${corte.numero_corte}: ${error.message}`, 'error');
    }
  };

  // Realizar corte parcial (después de autorización)
  const realizarCorteParcial = async () => {
    try {
      console.log("Realizando corte parcial...");

      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      console.log("🔍 userData completo del localStorage:", userData);
      const usuario = userData.usuario || 'Sistema';
      console.log("🔍 Usuario extraído:", usuario);
      console.log("🔍 Usuario enviado desde frontend (corte parcial):", usuario);

      const systemConfig = JSON.parse(localStorage.getItem('systemConfig') || '{}');
      const fondoCaja = parseFloat(systemConfig.fondoCaja || 0);

      const datosCorte = {
        tipo_corte: 'parcial',
        arqueo: arqueoCaja,
        opciones: opcionesImpresion,
        usuario: usuario,
        fondo_caja: fondoCaja
      };

      const response = await API.cortesCaja.realizarCorte(datosCorte);

      if (response.success) {
        console.log("Corte parcial realizado exitosamente:", response.data);

        let mensaje = `Corte parcial #${response.data.numero_corte} realizado exitosamente`;

        if (response.ticket) {
          mensaje += '\n\nSe ha generado el ticket de corte parcial.';
          mostrarAlertaPersonalizada(mensaje, 'success');

          // Imprimir ticket automáticamente
          try {
            const impresionExitosa = await imprimirTicketCorte(response.ticket);
            if (impresionExitosa) {
              mostrarAlertaPersonalizada("Ticket de corte parcial impreso exitosamente", 'success');
            }
          } catch (errorImpresion) {
            console.error("Error al imprimir:", errorImpresion);
            mostrarAlertaPersonalizada("Corte realizado, pero hubo un error al imprimir el ticket", 'warning');
          }
        } else {
          mostrarAlertaPersonalizada(mensaje, 'success');
        }

        // Recargar información
        cargarInformacionInicial();
      } else {
        console.error("Error al realizar corte parcial:", response.message);
        mostrarAlertaPersonalizada("Error al realizar corte parcial: " + response.message, 'error');
      }
    } catch (error) {
      console.error("Error al realizar corte parcial:", error);
      mostrarAlertaPersonalizada("Error de comunicación al realizar corte parcial", 'error');
    }
  };

  // Constantes de formato (mismo patrón que tickets de venta)
  const anchoTicket = 36;
  const margenIzquierdo = '  ';

  const centrarTextoTicket = (texto, ancho = anchoTicket) => {
    const textoTruncado = texto.length > ancho ? texto.substring(0, ancho) : texto;
    const espacios = Math.max(0, Math.floor((ancho - textoTruncado.length) / 2));
    return margenIzquierdo + (' '.repeat(espacios) + textoTruncado).padEnd(ancho);
  };

  const conMargen = (texto) => margenIzquierdo + texto;

  const formatearLineaTicket = (texto, valor = '', ancho = anchoTicket) => {
    const totalLength = texto.length + valor.length;
    const espacios = ancho - totalLength;
    return margenIzquierdo + texto + ' '.repeat(Math.max(0, espacios)) + valor;
  };

  const centrarTextoLargo = (texto, ancho = anchoTicket) => {
    const partes = texto.split(/\r?\n/);
    const resultado = [];
    partes.forEach(parte => {
      const parteTrimmed = parte.trim();
      if (!parteTrimmed) return;
      if (parteTrimmed.length <= ancho) {
        resultado.push(centrarTextoTicket(parteTrimmed, ancho));
      } else {
        const palabras = parteTrimmed.split(' ');
        let lineaActual = '';
        palabras.forEach(palabra => {
          const nueva = lineaActual ? `${lineaActual} ${palabra}` : palabra;
          if (nueva.length <= ancho) {
            lineaActual = nueva;
          } else {
            if (lineaActual) resultado.push(centrarTextoTicket(lineaActual, ancho));
            lineaActual = palabra;
          }
        });
        if (lineaActual) resultado.push(centrarTextoTicket(lineaActual, ancho));
      }
    });
    return resultado;
  };

  const formatearMoneda = (numero) => {
    return numero.toLocaleString('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Función para mostrar alertas personalizadas bonitas
  const mostrarAlertaPersonalizada = (mensaje, tipo = 'info', duracion = 4000) => {
    // Crear el contenedor de la alerta
    const alertaContainer = document.createElement('div');
    alertaContainer.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 10000;
      max-width: 400px;
      min-width: 300px;
      background: white;
      border-radius: 12px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
      border-left: 4px solid ${tipo === 'success' ? '#10b981' : tipo === 'error' ? '#ef4444' : tipo === 'warning' ? '#f59e0b' : '#3b82f6'};
      transform: translateX(420px);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      opacity: 0;
      overflow: hidden;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    `;

    // Crear el icono según el tipo
    const iconos = {
      success: '✅',
      error: '❌',
      warning: '⚠️',
      info: 'ℹ️'
    };

    // Crear el contenido de la alerta
    alertaContainer.innerHTML = `
      <div style="padding: 16px; display: flex; align-items: flex-start; gap: 12px;">
        <div style="font-size: 20px; flex-shrink: 0; margin-top: 2px;">
          ${iconos[tipo]}
        </div>
        <div style="flex: 1; min-width: 0;">
          <div style="font-weight: 600; color: #1f2937; font-size: 14px; margin-bottom: 4px; line-height: 1.4;">
            ${tipo === 'success' ? 'Éxito' : tipo === 'error' ? 'Error' : tipo === 'warning' ? 'Advertencia' : 'Información'}
          </div>
          <div style="color: #6b7280; font-size: 13px; line-height: 1.5; white-space: pre-line;">
            ${mensaje}
          </div>
        </div>
        <button onclick="this.parentElement.parentElement.remove()" style="
          background: transparent;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          font-size: 18px;
          padding: 0;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          flex-shrink: 0;
          transition: all 0.2s;
        " onmouseover="this.style.background='#f3f4f6'; this.style.color='#374151';" onmouseout="this.style.background='transparent'; this.style.color='#9ca3af';">
          ×
        </button>
      </div>
    `;

    // Agregar al DOM
    document.body.appendChild(alertaContainer);

    // Animar entrada
    setTimeout(() => {
      alertaContainer.style.transform = 'translateX(0)';
      alertaContainer.style.opacity = '1';
    }, 50);

    // Auto-remover después de la duración especificada
    const timeoutId = setTimeout(() => {
      alertaContainer.style.transform = 'translateX(420px)';
      alertaContainer.style.opacity = '0';
      setTimeout(() => {
        if (document.body.contains(alertaContainer)) {
          document.body.removeChild(alertaContainer);
        }
      }, 300);
    }, duracion);

    // Pausar auto-remove al hacer hover
    alertaContainer.addEventListener('mouseenter', () => {
      clearTimeout(timeoutId);
    });

    // Reanudar auto-remove al salir del hover
    alertaContainer.addEventListener('mouseleave', () => {
      setTimeout(() => {
        alertaContainer.style.transform = 'translateX(420px)';
        alertaContainer.style.opacity = '0';
        setTimeout(() => {
          if (document.body.contains(alertaContainer)) {
            document.body.removeChild(alertaContainer);
          }
        }, 300);
      }, 2000);
    });

    return alertaContainer;
  };

  // Función auxiliar para imprimir con fallbacks (reutilizable)
  const enviarImpresion = async (electronApi, printerName, ticketHtmlContent, ticketContent, etiqueta = '') => {
    const tag = etiqueta ? `[${etiqueta}] ` : '';
    let resultado = null;

    try {
      console.log(`🖨️ ${tag}Intentando método print-document...`);
      resultado = await Promise.race([
        electronApi.invoke('print-document', {
          printerName,
          htmlContent: ticketHtmlContent,
          silent: true,
          options: { printBackground: true, margin: { marginType: 'none' } }
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout en print-document')), 10000))
      ]);
      if (resultado && resultado.success) {
        console.log(`✅ ${tag}Impreso exitosamente con print-document`);
        return resultado;
      }
      throw new Error(resultado?.message || 'print-document falló');
    } catch (error1) {
      console.log(`❌ ${tag}Error con print-document: ${error1.message}`);
      try {
        console.log(`🖨️ ${tag}Intentando método print-receipt...`);
        resultado = await Promise.race([
          electronApi.invoke('print-receipt', { printer: printerName, content: ticketContent }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout en print-receipt')), 8000))
        ]);
        if (resultado && resultado.success) {
          console.log(`✅ ${tag}Impreso exitosamente con print-receipt`);
          return resultado;
        }
        throw new Error(resultado?.message || 'print-receipt falló');
      } catch (error2) {
        console.log(`❌ ${tag}Error con print-receipt: ${error2.message}`);
        try {
          console.log(`🖨️ ${tag}Intentando método print-simple-text...`);
          resultado = await Promise.race([
            electronApi.invoke('print-simple-text', { printerName, textContent: ticketContent, silent: true }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout en print-simple-text')), 6000))
          ]);
          if (resultado && resultado.success) {
            console.log(`✅ ${tag}Impreso exitosamente con print-simple-text`);
            return resultado;
          }
          throw new Error(resultado?.message || 'print-simple-text falló');
        } catch (error3) {
          throw new Error(`Todos los métodos fallaron:\n1. print-document: ${error1.message}\n2. print-receipt: ${error2.message}\n3. print-simple-text: ${error3.message}`);
        }
      }
    }
  };

  const imprimirTicketCorte = async (ticket) => {
    try {
      console.log("🖨️ Iniciando impresión del ticket de corte...");

      const electronApi = window.electron ? window.electron.ipcRenderer : null;
      if (!electronApi) {
        throw new Error('Electron API no está disponible. Esta función requiere la aplicación de escritorio Electron.');
      }

      const totalVentas = ticket.totales?.total_ventas || 0;
      const cuentas = ticket.cuentas || {};
      const cuentasPorTipo = cuentas.por_tipo || {};

      let seccionCuentasTipo = '';
      const tipos = [
        { key: 'mesa', label: 'MESA' },
        { key: 'llevar', label: 'LLEVAR' },
        { key: 'domicilio', label: 'DOMICILIO' },
        { key: 'uber', label: 'UBER EATS' },
        { key: 'rappi', label: 'RAPPI' },
        { key: 'delibot', label: 'DELIBOT' }
      ];

      tipos.forEach(({ key, label }) => {
        const datos = cuentasPorTipo[key] || { cantidad: 0, monto: 0, porcentaje: 0 };
        seccionCuentasTipo += conMargen(`CUENTAS '${label}':\n`);
        seccionCuentasTipo += conMargen(`  ${datos.cantidad.toString().padStart(4)}  ${datos.porcentaje.toFixed(2).padStart(6)}%  $${formatearMoneda(datos.monto).padStart(10)}\n`);
      });

      const totalEfectivo = ticket.totales?.total_efectivo || 0;
      const fondoCaja = ticket.totales?.fondo_caja || 0;
      const gastosEfectivo = ticket.totales?.gastos_efectivo || 0;
      const diferenciaEfectivo = ticket.totales?.diferencia_efectivo || 0;
      const formasPago = ticket.formas_pago || {};
      const estadisticas = ticket.estadisticas || {};
      const arqueo = ticket.arqueo || [];
      const ventasPorUsuario = ticket.ventas_por_usuario || {};
      const gastos = ticket.gastos || [];
      const platillosCancelados = ticket.platillos_cancelados || [];
      const totalCancelados = ticket.total_cancelados || 0;
      const numerosDelivery = ticket.numeros_delivery || {};

      let seccionArqueo = '';
      arqueo.forEach(item => {
        if (item.cantidad > 0) {
          const den = `$${item.denominacion}`;
          const cant = `x ${item.cantidad}`;
          const sub = `$${formatearMoneda(item.subtotal)}`;
          seccionArqueo += conMargen(`${den.padEnd(8)} ${cant.padEnd(6)} = ${sub.padStart(10)}\n`);
        }
      });

      let seccionGastos = '';
      if (gastos.length > 0) {
        gastos.forEach(gasto => {
          const conceptoLinea = gasto.concepto.length > 20 ? gasto.concepto.substring(0, 20) : gasto.concepto;
          seccionGastos += `${formatearLineaTicket(conceptoLinea, '$' + formatearMoneda(gasto.monto))}\n`;
          seccionGastos += conMargen(`  ${gasto.hora} - ${gasto.usuario}\n`);
        });
      } else {
        seccionGastos += conMargen('SIN GASTOS REGISTRADOS\n');
      }

      let seccionCancelados = '';
      if (platillosCancelados.length > 0) {
        platillosCancelados.forEach(item => {
          const platilloLinea = item.platillo.length > 16 ? item.platillo.substring(0, 16) : item.platillo;
          seccionCancelados += conMargen(`${item.cantidad} ${platilloLinea.padEnd(16)} $${formatearMoneda(item.subtotal).padStart(8)}\n`);
          seccionCancelados += conMargen(`  #${item.numero_pedido} ${item.hora}\n`);
          if (item.comentario) {
            const comentarioLinea = item.comentario.length > 28 ? item.comentario.substring(0, 28) + '..' : item.comentario;
            seccionCancelados += conMargen(`  ${comentarioLinea}\n`);
          }
          seccionCancelados += conMargen(`  Autorizo: ${item.supervisor}\n`);
        });
      } else {
        seccionCancelados += conMargen('SIN CANCELACIONES\n');
      }

      let seccionNumerosDelivery = '';
      const tiposDelivery = [
        { key: 'uber', label: 'UBER EATS' },
        { key: 'rappi', label: 'RAPPI' },
        { key: 'delibot', label: 'DELIBOT' }
      ];

      tiposDelivery.forEach(({ key, label }) => {
        const cuentasDel = numerosDelivery[key] || [];
        if (cuentasDel.length > 0) {
          seccionNumerosDelivery += conMargen(`${label}:\n`);
          let totalTipo = 0;
          cuentasDel.forEach(cuenta => {
            seccionNumerosDelivery += conMargen(`  #${cuenta.numero.toString().padEnd(5)} ${cuenta.hora.padEnd(6)} $${formatearMoneda(cuenta.total).padStart(9)}\n`);
            totalTipo += cuenta.total;
          });
          seccionNumerosDelivery += `${formatearLineaTicket('  TOTAL ' + label + ':', '$' + formatearMoneda(totalTipo))}\n`;
          seccionNumerosDelivery += '\n';
        }
      });

      if (seccionNumerosDelivery === '') {
        seccionNumerosDelivery = conMargen('SIN PEDIDOS DE DELIVERY\n');
      }

      let seccionVentasUsuario = '';
      Object.entries(ventasPorUsuario).forEach(([usuario, ventas]) => {
        seccionVentasUsuario += conMargen(`${usuario.toUpperCase()}\n`);
        seccionVentasUsuario += `${formatearLineaTicket('  EFECTIVO:', '$' + formatearMoneda(ventas.efectivo || 0))}\n`;
        seccionVentasUsuario += `${formatearLineaTicket('  TARJETA:', '$' + formatearMoneda(ventas.tarjeta || 0))}\n`;
        if ((ventas.cupon || 0) + (ventas.cortesia || 0) > 0) {
          seccionVentasUsuario += `${formatearLineaTicket('  DESC/CORTESIAS:', '$' + formatearMoneda((ventas.cupon || 0) + (ventas.cortesia || 0)))}\n`;
        }
        if (ventas.uber > 0) seccionVentasUsuario += `${formatearLineaTicket('  UBER:', '$' + formatearMoneda(ventas.uber))}\n`;
        if (ventas.rappi > 0) seccionVentasUsuario += `${formatearLineaTicket('  RAPPI:', '$' + formatearMoneda(ventas.rappi))}\n`;
        if (ventas.delibot > 0) seccionVentasUsuario += `${formatearLineaTicket('  DELIBOT:', '$' + formatearMoneda(ventas.delibot))}\n`;
        seccionVentasUsuario += `${formatearLineaTicket('  TOTAL:', '$' + formatearMoneda(ventas.total || 0))}\n`;
        seccionVentasUsuario += '\n';
      });

      // Construir encabezado con misma configuración que tickets de venta
      const camposEncabezado = (() => {
        try { return JSON.parse(localStorage.getItem('ticket_campos_encabezado_cuenta') || '{}'); }
        catch { return {}; }
      })();
      const mostrarCampo = (campo) => camposEncabezado[campo] !== undefined ? camposEncabezado[campo] : true;

      const lineasEncabezado = [];
      if (mostrarCampo('nombre_negocio')) {
        const nombreCompleto = mostrarCampo('nombre_sucursal') && infoNegocio.nombre_sucursal
          ? `${infoNegocio.nombre_negocio} ${infoNegocio.nombre_sucursal}`
          : infoNegocio.nombre_negocio;
        lineasEncabezado.push(...centrarTextoLargo(nombreCompleto));
      } else if (mostrarCampo('nombre_sucursal') && infoNegocio.nombre_sucursal) {
        lineasEncabezado.push(...centrarTextoLargo(infoNegocio.nombre_sucursal));
      }
      if (mostrarCampo('direccion_linea1') && infoNegocio.direccion_linea1) lineasEncabezado.push(...centrarTextoLargo(infoNegocio.direccion_linea1));
      if (mostrarCampo('direccion_linea2') && infoNegocio.direccion_linea2) lineasEncabezado.push(...centrarTextoLargo(infoNegocio.direccion_linea2));
      if (mostrarCampo('razon_social') && infoNegocio.razon_social) lineasEncabezado.push(...centrarTextoLargo(infoNegocio.razon_social));
      if (mostrarCampo('rfc') && infoNegocio.rfc) lineasEncabezado.push(...centrarTextoLargo(`RFC: ${infoNegocio.rfc}`));
      if (mostrarCampo('telefono') && infoNegocio.telefono) lineasEncabezado.push(...centrarTextoLargo(`Tel: ${infoNegocio.telefono}`));

      const tipoCorteTexto = ticket.tipo === 'corte_parcial' ? 'CORTE DE CAJA PARCIAL' : 'CORTE DE CAJA FINAL';

      const ticketContent = `${conMargen('='.repeat(anchoTicket))}
${lineasEncabezado.join('\n')}
${conMargen('='.repeat(anchoTicket))}
${centrarTextoTicket(`** ${tipoCorteTexto} **`)}
${centrarTextoTicket(`NUMERO: ${ticket.header.numero_corte}`)}
${conMargen('='.repeat(anchoTicket))}
${conMargen(`EFECTUADO: ${ticket.header.fecha} ${ticket.header.hora}`)}
${conMargen(`POR: ${ticket.header.usuario || 'Sistema'}`)}
${conMargen(`DE: ${ticket.header.fechaAnterior} ${ticket.header.horaAnterior}`)}
${conMargen(` A: ${ticket.header.fecha} ${ticket.header.hora}`)}
${conMargen('='.repeat(anchoTicket))}
${formatearLineaTicket('TOTAL DE VENTAS:', '$' + formatearMoneda(totalVentas))}
${conMargen('='.repeat(anchoTicket))}
${conMargen('-'.repeat(anchoTicket))}
${formatearLineaTicket('CUENTAS ABIERTAS:', (cuentas.total_periodo || 0).toString())}
${formatearLineaTicket('PEND. DE COBRO:', (cuentas.pendientes_cobro || 0).toString())}
${seccionCuentasTipo}${conMargen('='.repeat(anchoTicket))}
${formatearLineaTicket('CUENTAS COBRADAS:', '$' + formatearMoneda(formasPago.total_cobrado || 0))}

${formatearLineaTicket('EFECTIVO:', '$' + formatearMoneda(formasPago.efectivo || 0))}
${formatearLineaTicket('TARJETA:', '$' + formatearMoneda(formasPago.tarjeta || 0))}
${formatearLineaTicket('UBER:', '$' + formatearMoneda(formasPago.uber || 0))}
${formatearLineaTicket('RAPPI:', '$' + formatearMoneda(formasPago.rappi || 0))}
${formatearLineaTicket('DELIBOT:', '$' + formatearMoneda(formasPago.delibot || 0))}
${formatearLineaTicket('DESC/CORTESIAS:', '$' + formatearMoneda((formasPago.cupon || 0) + (formasPago.cortesia || 0)))}
${conMargen('='.repeat(anchoTicket))}
${formatearLineaTicket('FONDO CAJA:', '$' + formatearMoneda(fondoCaja))}
${formatearLineaTicket('CONTADO AL CORTE:', '$' + formatearMoneda(totalEfectivo))}
${formatearLineaTicket('GASTOS EFECTIVO:', '$' + formatearMoneda(gastosEfectivo))}
${formatearLineaTicket('DIF. EFECTIVO:', '$' + formatearMoneda(diferenciaEfectivo))}
${conMargen('='.repeat(anchoTicket))}
${formatearLineaTicket('TICKET PROMEDIO:', '$' + formatearMoneda(estadisticas.ticket_promedio || 0))}
${formatearLineaTicket('TICKET PROM/MESA:', '$' + formatearMoneda(estadisticas.ticket_promedio_mesa || 0))}
${formatearLineaTicket('TICKET PROM/PERS:', '$' + formatearMoneda(estadisticas.ticket_promedio_persona || 0))}
${formatearLineaTicket('PERS. ATENDIDAS:', (estadisticas.personas_atendidas || 0).toString())}
${conMargen('='.repeat(anchoTicket))}
${centrarTextoTicket('DETALLE EFECTIVO RECIBIDO')}
${conMargen('='.repeat(anchoTicket))}
${seccionArqueo}${conMargen('-'.repeat(anchoTicket))}
${formatearLineaTicket('TOTAL EFECTIVO:', '$' + formatearMoneda(totalEfectivo))}
${conMargen('='.repeat(anchoTicket))}
${centrarTextoTicket('GASTOS DE CAJA')}
${conMargen('='.repeat(anchoTicket))}
${seccionGastos}${conMargen('-'.repeat(anchoTicket))}
${formatearLineaTicket('TOTAL GASTOS:', '$' + formatearMoneda(gastosEfectivo))}
${conMargen('='.repeat(anchoTicket))}
${centrarTextoTicket('PLATILLOS CANCELADOS')}
${conMargen('='.repeat(anchoTicket))}
${seccionCancelados}${conMargen('-'.repeat(anchoTicket))}
${formatearLineaTicket('TOTAL CANCELADO:', '$' + formatearMoneda(totalCancelados))}
${conMargen('='.repeat(anchoTicket))}
${centrarTextoTicket('CUENTAS DELIVERY')}
${conMargen('='.repeat(anchoTicket))}
${seccionNumerosDelivery}${conMargen('='.repeat(anchoTicket))}
${centrarTextoTicket('VENTAS POR USUARIO')}
${conMargen('='.repeat(anchoTicket))}
${seccionVentasUsuario}`;

      // Construir HTML con logo si está configurado (mismo patrón que tickets de venta)
      const ticketLogoBase64 = localStorage.getItem('ticket_logo_base64');
      const ticketLogoEnabled = localStorage.getItem('ticket_logo_enabled') === 'true';
      const logoHtml = ticketLogoBase64 && ticketLogoEnabled
        ? `<div style="padding-left:0;width:280px;text-align:center;margin-bottom:4px;"><img src="${ticketLogoBase64}" style="max-width:140px;height:auto;" /></div>`
        : '';
      const ticketHtmlContent = `${logoHtml}<pre style="font-family: monospace; white-space: pre-wrap; font-size: 13px;">${ticketContent}</pre>`;

      const printers = await electronApi.invoke('get-all-printers');
      const defaultPrinter = printers.find(p => p.isDefault) || printers[0];

      if (!defaultPrinter) {
        throw new Error("No se encontró impresora predeterminada para imprimir el ticket");
      }

      console.log("🖨️ Imprimiendo ticket en:", defaultPrinter.name);

      await enviarImpresion(electronApi, defaultPrinter.name, ticketHtmlContent, ticketContent);

      // Verificar si se debe imprimir copia según systemConfig
      try {
        const systemConfig = JSON.parse(localStorage.getItem('systemConfig') || '{}');
        const imprimirCopiaCorteFinal = systemConfig.imprimirCopiaCorteFinal === 1 || systemConfig.imprimirCopiaCorteFinal === '1';

        if (imprimirCopiaCorteFinal) {
          console.log("📄 Imprimiendo COPIA del corte de caja...");
          await new Promise(resolve => setTimeout(resolve, 500));
          try {
            await enviarImpresion(electronApi, defaultPrinter.name, ticketHtmlContent, ticketContent, 'COPIA');
          } catch (errorCopia) {
            console.warn("⚠️ No se pudo imprimir la copia del corte:", errorCopia.message);
          }
        }
      } catch (configError) {
        console.warn("⚠️ Error al leer configuración de copia:", configError);
      }

      return true;

    } catch (error) {
      console.error("Error al imprimir ticket de corte:", error);
      mostrarAlertaPersonalizada("Error al imprimir ticket: " + error.message, 'error');
      return false;
    }
  };

  // Mostrar ticket de corte final
  const mostrarTicketCorte = (ticket) => {
    let ticketHtml = `
      <div style="font-family: monospace; white-space: pre-line; max-width: 400px;">
        <div style="text-align: center; font-weight: bold; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 15px;">
          ${ticket.header.titulo}
          Corte #${ticket.header.numero_corte}
          ${ticket.header.fecha} - ${ticket.header.hora}
          Usuario: ${ticket.header.usuario}
        </div>

        <div style="margin-bottom: 15px;">
          <strong>ARQUEO DE CAJA:</strong>
          ${ticket.arqueo.map(item =>
      `$${item.denominacion} x ${item.cantidad} = $${item.subtotal.toFixed(2)}`
    ).join('\n')}
        </div>

        <div style="border-top: 1px solid #333; padding-top: 10px; margin-bottom: 15px;">
          <strong>TOTALES:</strong>
          Total Efectivo: $${ticket.totales.total_efectivo.toFixed(2)}
          Total Ventas: $${ticket.totales.total_ventas.toFixed(2)}
          Total Cuentas: ${ticket.totales.total_mesas}
        </div>

        <div style="border-top: 1px solid #333; padding-top: 10px; margin-bottom: 15px;">
          <strong>DESGLOSE DE CUENTAS POR TIPO:</strong>
          ${Object.entries(ticket.datos_bd?.cuentas_por_tipo || ticket.cuentas_por_tipo || {}).map(([tipo, datos]) => {
      const tipoCaps = tipo.toUpperCase();
      const cantidad = datos.cantidad || 0;
      const monto = datos.monto || 0;
      const porcentaje = datos.porcentaje_cantidad || datos.porcentaje || 0;
      return `${tipoCaps}: ${cantidad} (${porcentaje.toFixed(2)}%) - $${monto.toFixed(2)}`;
    }).join('\n')}
        </div>

        ${ticket.totales && ticket.totales.gastos_efectivo !== undefined ? `
        <div style="border-top: 1px solid #333; padding-top: 10px;">
          <strong>GASTOS DEL DÍA:</strong>
          Total Gastos: $${ticket.totales.gastos_efectivo.toFixed(2)}
        </div>
        ` : ''}

        <div style="text-align: center; margin-top: 20px; font-size: 12px; color: #666;">
          Ticket generado automáticamente
          ${new Date().toLocaleString()}
        </div>
      </div>
    `;

    // Crear modal para mostrar el ticket
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(0,0,0,0.8); display: flex; justify-content: center; align-items: center;
      z-index: 10000;
    `;

    const ticketContainer = document.createElement('div');
    ticketContainer.style.cssText = `
      background: white; padding: 20px; border-radius: 10px; max-height: 80vh; overflow-y: auto;
      box-shadow: 0 10px 30px rgba(0,0,0,0.3);
    `;

    const closeButton = document.createElement('button');
    closeButton.textContent = 'Cerrar';
    closeButton.style.cssText = `
      background: #667eea; color: white; border: none; padding: 10px 20px;
      border-radius: 5px; cursor: pointer; margin-top: 20px; display: block; margin-left: auto; margin-right: auto;
    `;
    closeButton.onclick = () => document.body.removeChild(modal);

    ticketContainer.innerHTML = ticketHtml;
    ticketContainer.appendChild(closeButton);
    modal.appendChild(ticketContainer);
    document.body.appendChild(modal);
  };

  // Solicitar confirmación para corte final
  const solicitarCorteFinal = () => {
    setMostrarConfirmCorteFinal(true);
  };

  // Realizar corte final (después de confirmación)
  const realizarCorteFinal = async () => {
    setMostrarConfirmCorteFinal(false);
    try {
      console.log("Realizando corte final...");

      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      console.log("🔍 userData completo del localStorage:", userData);
      const usuario = userData.usuario || 'Sistema';
      console.log("🔍 Usuario extraído:", usuario);
      console.log("🔍 Usuario enviado desde frontend (corte final):", usuario);

      const systemConfig = JSON.parse(localStorage.getItem('systemConfig') || '{}');
      const fondoCaja = parseFloat(systemConfig.fondoCaja || 0);

      const datosCorte = {
        tipo_corte: 'final',
        arqueo: arqueoCaja,
        opciones: opcionesImpresion,
        usuario: usuario,
        fondo_caja: fondoCaja
      };

      const response = await API.cortesCaja.realizarCorte(datosCorte);

      if (response.success) {
        console.log("Corte final realizado exitosamente:", response.data);

        let mensaje = `Corte final #${response.data.numero_corte} realizado exitosamente`;

        if (response.ticket) {
          mensaje += '\n\nSe ha generado el ticket de finalización.';
          mostrarAlertaPersonalizada(mensaje, 'success');

          // Imprimir ticket automáticamente
          try {
            const impresionExitosa = await imprimirTicketCorte(response.ticket);
            if (impresionExitosa) {
              mostrarAlertaPersonalizada("Ticket de corte final impreso exitosamente", 'success');
            }
          } catch (errorImpresion) {
            console.error("Error al imprimir:", errorImpresion);
            mostrarAlertaPersonalizada("Corte realizado, pero hubo un error al imprimir el ticket", 'warning');
          }

          // También mostrar ticket en pantalla
          // mostrarTicketCorte(response.ticket);
        } else {
          mostrarAlertaPersonalizada(mensaje, 'success');
        }

        // Limpiar arqueo después del corte final
        setArqueoCaja({
          '1000.00': 0, '500.00': 0, '200.00': 0, '100.00': 0, '50.00': 0,
          '20.00': 0, '10.00': 0, '5.00': 0, '2.00': 0, '1.00': 0, '0.50': 0
        });

        // Recargar información
        cargarInformacionInicial();
      } else {
        console.error("Error al realizar corte final:", response.message);

        // Mostrar mensaje específico según el tipo de error
        if (response.cuentas_abiertas) {
          mostrarAlertaPersonalizada(`NO SE PUEDE REALIZAR EL CORTE FINAL\n\n${response.message}\n\nPor favor, cierre y liquide todas las cuentas antes de continuar.`, 'error');
        } else if (response.pendientes_liquidar !== undefined || response.pendientes_liquidar_hoy !== undefined) {
          // Error por pedidos cerrados pero pendientes de liquidar
          let mensaje = `❌ NO SE PUEDE REALIZAR EL CORTE FINAL\n\n${response.message}`;

          if (response.periodo_consultado) {
            mensaje += `\n\nPeríodo consultado:\nDesde: ${response.periodo_consultado.desde}\nHasta: ${response.periodo_consultado.hasta}`;
          }

          if (response.es_primer_corte) {
            mensaje += "\n\n📝 Nota: Este sería el primer corte del sistema.";
          }

          mensaje += "\n\n⚠️ ACCIÓN REQUERIDA: Vaya a la sección de ventas y liquide todos los pedidos cerrados.";

          mostrarAlertaPersonalizada(mensaje.replace('❌ ', ''), 'error');
        } else if (response.pedidos_periodo !== undefined || response.pedidos_hoy !== undefined) {
          // Error por falta de pedidos en el período
          let mensaje = `❌ NO SE PUEDE REALIZAR EL CORTE FINAL\n\n${response.message}`;

          if (response.periodo_consultado) {
            mensaje += `\n\nPeríodo consultado:\nDesde: ${response.periodo_consultado.desde}\nHasta: ${response.periodo_consultado.hasta}`;
          }

          if (response.es_primer_corte) {
            mensaje += "\n\n📝 Nota: Este sería el primer corte del sistema.";
          }

          mostrarAlertaPersonalizada(mensaje.replace('❌ ', ''), 'error');
        } else if (response.ventas_periodo !== undefined || response.ventas_hoy !== undefined) {
          // Error por pedidos sin liquidar
          mostrarAlertaPersonalizada(`NO SE PUEDE REALIZAR EL CORTE FINAL\n\n${response.message}\n\n📝 Los pedidos deben estar cerrados y liquidados para incluirse en el corte.`, 'error');
        } else {
          mostrarAlertaPersonalizada("Error al realizar corte final: " + response.message, 'error');
        }
      }
    } catch (error) {
      console.error("Error al realizar corte final:", error);
      mostrarAlertaPersonalizada("Error de comunicación al realizar corte final", 'error');
    }
  };

  return (
    <div className="corte-caja-container">
      <Header
        titulo="Corte de Caja"
        color="#667eea"
        showSystemInfo={true}
        showBackButton={true}
      />

      {/* Contenido principal en grid */}
      <div className="corte-main-content">

        {/* Sección izquierda: Arqueo de Caja */}
        <div className="seccion-arqueo">
          <div className="panel-titulo">
            <FaCalculator className="panel-icono" />
            <h2>Arqueo de Caja</h2>
          </div>

          <div className="arqueo-grid">
            {Object.entries(arqueoCaja).map(([denominacion, cantidad]) => (
              <div key={denominacion} className="arqueo-item">
                <div className="denominacion">${denominacion}</div>
                <input
                  type="number"
                  min="0"
                  value={cantidad}
                  onChange={(e) => handleArqueoChange(e, denominacion)}
                  onFocus={handleInputFocus}
                  onKeyDown={(e) => handleKeyDown(e, denominacion)}
                  className="cantidad-input"
                  placeholder="0"
                  data-denominacion={denominacion}
                />
                <div className="subtotal">
                  ${(parseFloat(denominacion) * cantidad).toFixed(2)}
                </div>
              </div>
            ))}
          </div>

          <div className="total-arqueo-destacado">
            <span>TOTAL EFECTIVO:</span>
            <span className="total-valor">${totalArqueo.toFixed(2)}</span>
          </div>
        </div>

        {/* Sección central: Información del Corte */}
        <div className="seccion-info">
          <div className="panel-titulo">
            <FaRegListAlt className="panel-icono" />
            <h2>Información del Corte</h2>
          </div>

          <div className="info-cards">
            <div className="info-card">
              <div className="card-label">Siguiente Corte</div>
              <div className="card-value"># {infoCorte.siguienteCorte}</div>
            </div>

            <div className="info-card">
              <div className="card-label">Último Corte Final</div>
              <div className="card-value">
                {infoCorte.ultimoCorte.fecha || 'Sin cortes anteriores'}
                {infoCorte.ultimoCorte.hora && <small>{infoCorte.ultimoCorte.hora}</small>}
              </div>
            </div>
          </div>

          {/* Opciones de impresión en formato compacto - OCULTO */}
          {/*
          <div className="opciones-seccion">
            <h3>
              <FaPrint className="seccion-icono" />
              Opciones de Impresión
            </h3>
            <div className="opciones-grid">
              {[
                { key: 'imprimirProductosVendidos', label: 'Productos Vendidos' },
                { key: 'imprimirProductosCancelados', label: 'Productos Cancelados' },
                { key: 'imprimirProductosMalaOrden', label: 'Mala Orden' },
                { key: 'imprimirProductosConGarantia', label: 'Con Garantía' },
                { key: 'imprimirProductosAgrupados', label: 'Agrupados' },
                { key: 'imprimirGastos', label: 'Gastos' },
                { key: 'imprimirCancelados', label: 'Cancelados' }
              ].map(({ key, label }) => (
                <label key={key} className="opcion-checkbox">
                  <input
                    type="checkbox"
                    name={key}
                    checked={opcionesImpresion[key]}
                    onChange={handleOpcionImpresionChange}
                  />
                  <span className="checkmark"></span>
                  <span className="label-text">{label}</span>
                </label>
              ))}
            </div>
          </div>
          */}

          {/* Botones principales de corte */}
          <div className="botones-principales" style={{display: 'flex', gap: '10px', flexDirection: 'row'}}>
            <button
              onClick={solicitarAutorizacionCorteParcial}
              className="btn-accion btn-parcial"
              style={{flex: '1'}}
            >
              <FaFileInvoiceDollar className="btn-icono" />
              <div className="btn-content">
                <div className="btn-titulo">Corte Parcial</div>
                <div className="btn-descripcion">Reporte sin cerrar turno</div>
              </div>
            </button>

            <button
              onClick={solicitarCorteFinal}
              className="btn-accion btn-final"
              style={{flex: '1'}}
            >
              <FaCashRegister className="btn-icono" />
              <div className="btn-content">
                <div className="btn-titulo">Corte Final</div>
                <div className="btn-descripcion">Cerrar turno y arqueo</div>
              </div>
            </button>
          </div>
        </div>

        {/* Sección derecha: Acciones y Consulta */}
        <div className="seccion-acciones">
          {/* Consulta de cortes anteriores */}
          <div className="consulta-section">
            <div className="panel-titulo small">
              <FaSearchDollar className="panel-icono" />
              <h3>Consultar Cortes Anteriores</h3>
            </div>

            <div className="consulta-form">
              <div className="fecha-selector">
                <FaCalendarAlt className="input-icono" />
                <input
                  type="date"
                  value={fechaConsulta}
                  onChange={handleFechaConsultaChange}
                  className="select-fecha"
                />
              </div>

              <button
                onClick={buscarCortesAnteriores}
                className="btn-consultar"
              >
                <FaListUl />
                Mostrar Cortes
              </button>
            </div>

            <div className="resultados-mini">
              {cortesAnteriores.length === 0 ? (
                <div className="no-resultados">
                  <FaRegListAlt />
                  <span>No hay cortes para mostrar</span>
                </div>
              ) : (
                <div className="lista-cortes">
                  {cortesAnteriores.map((corte) => (
                    <div key={corte.id} className="corte-item">
                      <div className="corte-header">
                        <span className="corte-numero">
                          #{corte.numero_corte}
                        </span>
                        <span className={`corte-tipo ${corte.tipo}`}>
                          {corte.tipo === 'final' ? 'FINAL' : 'PARCIAL'}
                        </span>
                      </div>
                      <div className="corte-info">
                        <div className="corte-fecha">
                          📅 {corte.fecha} {corte.hora}
                        </div>
                        <div className="corte-usuario">
                          👤 {corte.usuario}
                        </div>
                        <div className="corte-totales">
                          <span>💰 Efectivo: ${formatearMoneda(corte.total_efectivo)}</span>
                          <span>🧾 Ventas: ${formatearMoneda(corte.total_ventas)}</span>
                        </div>
                        <div className="corte-mesas">
                          🏠 {corte.total_mesas} mesa{corte.total_mesas !== 1 ? 's' : ''}
                        </div>
                      </div>
                      <div className="corte-acciones">
                        <button
                          onClick={() => reimprimirCorte(corte)}
                          className="btn-reimprimir"
                          title="Reimprimir corte"
                        >
                          <FaPrint />
                          Reimprimir
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* Modal de autorización para corte parcial */}
      <AutorizacionModal
        isOpen={mostrarAutorizacion}
        onClose={cerrarAutorizacion}
        onAuthorize={handleAutorizacionExitosa}
        accion="realizar corte parcial"
        perfilRequerido="corteParcial"
        titulo="Autorización para Corte Parcial"
        descripcion="Se requiere autorización para realizar un corte parcial de caja"
      />

      {/* Modal de confirmación para corte final */}
      {mostrarConfirmCorteFinal && (
        <div className="confirm-overlay" onClick={() => setMostrarConfirmCorteFinal(false)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon">
              <FaCashRegister />
            </div>
            <h3 className="confirm-title">Corte Final</h3>
            <p className="confirm-message">
              ¿Está seguro de realizar el corte final? Esta acción validará que no haya cuentas abiertas y que existan ventas desde el último corte.
            </p>
            <div className="confirm-buttons">
              <button className="confirm-btn confirm-btn--cancel" onClick={() => setMostrarConfirmCorteFinal(false)}>
                Cancelar
              </button>
              <button className="confirm-btn confirm-btn--accept" onClick={realizarCorteFinal}>
                Sí, realizar corte
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorteCaja;